import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { can } from '../../api/client';
import { fetchOperationsSettings, saveOperationsSettings, type OperationsSettings } from '../../api/ops';
import { Card, Field } from '../../ui/components';
import { useMe } from '../auth/session';

/**
 * Settings → Reports / AFM (automatic reports; baselines live with their project) and Settings → System (raw data
 * retention). Everyone with report.view sees the values; only a global settings.manage holder changes them.
 */
export function OperationsSettingsCard({ section }: { section: 'reports' | 'system' }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const me = useMe().data;
  const editable = can(me, 'settings.manage');
  const q = useQuery({ queryKey: ['settings', 'operations'], queryFn: fetchOperationsSettings });
  const [form, setForm] = useState<OperationsSettings | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (q.data) setForm(q.data);
  }, [q.data]);
  const save = useMutation({
    mutationFn: saveOperationsSettings,
    onSuccess: () => {
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['settings', 'operations'] });
    },
  });
  if (q.isError)
    return (
      <Card title={t(('opsSettings.tabs.' + section) as 'opsSettings.tabs.system')}>
        <p className="muted">{t('opsSettings.noAccess')}</p>
      </Card>
    );
  if (!form) return null;
  const set = (patch: Partial<OperationsSettings>) => {
    setSaved(false);
    setForm({ ...form, ...patch });
  };
  const days = (v: string) => (v.trim() === '' ? null : Math.max(30, Math.min(3650, Number(v) || 30)));
  const actions = editable && (
    <div className="form-actions">
      {saved && <span className="form-ok">{t('common.saved')}</span>}
      {save.isError && <span className="form-error">{t('opsSettings.saveFailed')}</span>}
      <button
        type="button"
        className="btn btn-primary"
        disabled={save.isPending}
        onClick={() => save.mutate(form)}
      >
        {t('common.save')}
      </button>
    </div>
  );
  if (section === 'reports')
    return (
      <div className="ops-stack">
        <Card title={t('opsSettings.reports.automatic')}>
          <p className="muted" style={{ marginTop: 0 }}>
            {t('opsSettings.reports.automaticHint')}
          </p>
          <label className="check-row">
            <input
              type="checkbox"
              checked={form.reports_automatic.annual}
              disabled={!editable}
              onChange={(e) =>
                set({ reports_automatic: { ...form.reports_automatic, annual: e.target.checked } })
              }
            />
            {t('opsSettings.reports.annual')}
          </label>
          <label className="check-row">
            <input
              type="checkbox"
              checked={form.reports_automatic.monthly}
              disabled={!editable}
              onChange={(e) =>
                set({ reports_automatic: { ...form.reports_automatic, monthly: e.target.checked } })
              }
            />
            {t('opsSettings.reports.monthly')}
          </label>
          <Field label={t('opsSettings.reports.locale')}>
            <select
              className="input"
              value={form.reports_automatic.locale}
              disabled={!editable}
              onChange={(e) =>
                set({
                  reports_automatic: { ...form.reports_automatic, locale: e.target.value as 'en' | 'ro' },
                })
              }
            >
              <option value="ro">Română</option>
              <option value="en">English</option>
            </select>
          </Field>
          {actions}
        </Card>
        <Card title={t('opsSettings.reports.baselines')}>
          <p className="muted" style={{ marginTop: 0 }}>
            {t('opsSettings.reports.baselinesHint')}
          </p>
          <Link className="btn" to="/reports/projects">
            {t('opsSettings.reports.openProjects')}
          </Link>
        </Card>
      </div>
    );
  const limit = form.command_rate_limit ?? { max_actions: 30, window_minutes: 10 };
  const int = (v: string, min: number, max: number) =>
    Math.max(min, Math.min(max, Math.trunc(Number(v)) || min));
  return (
    <div className="ops-stack">
      {/* default per-user command limit of client users (site administrator, operator); per-client override in
          Clients & sites (user request 2026-10-10) */}
      <Card title={t('opsSettings.system.commandLimit')}>
        <p className="muted" style={{ marginTop: 0 }}>
          {t('opsSettings.system.commandLimitHint')}
        </p>
        <div className="form-grid">
          <Field label={t('opsSettings.system.maxActions')}>
            <input
              className="input"
              type="number"
              min={1}
              max={1000}
              value={limit.max_actions}
              disabled={!editable}
              onChange={(e) =>
                set({ command_rate_limit: { ...limit, max_actions: int(e.target.value, 1, 1000) } })
              }
            />
          </Field>
          <Field label={t('opsSettings.system.windowMinutes')}>
            <input
              className="input"
              type="number"
              min={1}
              max={1440}
              value={limit.window_minutes}
              disabled={!editable}
              onChange={(e) =>
                set({ command_rate_limit: { ...limit, window_minutes: int(e.target.value, 1, 1440) } })
              }
            />
          </Field>
        </div>
        {actions}
      </Card>
      <Card title={t('opsSettings.system.retention')}>
        <p className="muted" style={{ marginTop: 0 }}>
          {t('opsSettings.system.retentionHint')}
        </p>
        <div className="form-grid">
          <Field label={t('opsSettings.system.rawDays')} hint={t('opsSettings.system.emptyKeeps')}>
            <input
              className="input"
              type="number"
              min={30}
              max={3650}
              value={form.raw_retention_days ?? ''}
              disabled={!editable}
              onChange={(e) => set({ raw_retention_days: days(e.target.value) })}
            />
          </Field>
          <Field label={t('opsSettings.system.presenceDays')} hint={t('opsSettings.system.emptyKeeps')}>
            <input
              className="input"
              type="number"
              min={30}
              max={3650}
              value={form.presence_retention_days ?? ''}
              disabled={!editable}
              onChange={(e) => set({ presence_retention_days: days(e.target.value) })}
            />
          </Field>
        </div>
        <p className="muted small">{t('opsSettings.system.aggregatesKept')}</p>
        {actions}
      </Card>
    </div>
  );
}
